import {
    fetchGetFundingRequirementsCreditFacility,
    fetchGetLoanDocumentsRequiredCreditFacility
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { getCurrentLocation } from '../../../../../iwRouter/HistoryProvider';
import { getQueryParameter } from '../../../../../iwRouter/LocationHelper';
import { deserialiseFundingRequirements } from '../../../../funding/fundingSteps/deserialiseFundingRequirements';
import { TFundingRequirementStatus } from '../RequirementsList/RequirementItem/RequirementItem';

type TLoanDocumentRequirement = {
    type: 'PERSONAL_GUARANTEE' | 'CREDIT_AGREEMENT' | 'REFINANCING_SIDE_LETTER';
    status: TFundingRequirementStatus;
};

const PENDING_POLL_INTERVAL_MS = 2000;

export function useGetFundingRequirements() {
    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');

    const { data, refetch } = useQuery({
        queryKey: [
            'fetchGetFundingRequirementsCreditFacility',
            stateKey,
            offerId,
            amount
        ],
        queryFn: () =>
            fetchGetFundingRequirementsCreditFacility({
                stateKey,
                offerId: offerId!,
                query: { cash_amount: Number(amount) }
            }),
        enabled: Boolean(offerId) && Boolean(amount),
        select: (response) => deserialiseFundingRequirements(response.data),
        suspense: true,
        // Poll while a requirement is mid-update (PENDING) so the list flips to
        // its settled state on its own; stop once nothing is pending.
        refetchInterval: (requirements) =>
            requirements?.some(({ status }) => status === 'PENDING')
                ? PENDING_POLL_INTERVAL_MS
                : false
    });

    return {
        fundingRequirements: data ?? [],
        refetch
    };
}

const DOCUMENT_TYPE_MAP: Record<string, TLoanDocumentRequirement['type']> = {
    guarantee: 'PERSONAL_GUARANTEE',
    master_loan_agreement: 'CREDIT_AGREEMENT',
    refinancing_side_letter: 'REFINANCING_SIDE_LETTER'
};

export function useGetLoanDocumentRequirements() {
    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');

    const { data } = useQuery({
        queryKey: [
            'fetchGetLoanDocumentsRequiredCreditFacility',
            stateKey,
            offerId,
            amount
        ],
        queryFn: () =>
            fetchGetLoanDocumentsRequiredCreditFacility({
                stateKey,
                offerId: offerId!,
                query: { cash_amount: Number(amount) }
            }),
        enabled: Boolean(offerId) && Boolean(amount),
        select: (response): TLoanDocumentRequirement[] =>
            response.data.required_loan_documents
                .filter((doc) => doc.type in DOCUMENT_TYPE_MAP)
                .map((doc) => ({
                    type: DOCUMENT_TYPE_MAP[doc.type]!,
                    status: doc.agreed ? 'COMPLETED' : 'REQUIRED'
                })),
        suspense: true
    });

    return { loanDocumentRequirements: data ?? [] };
}
